import { createElement, type CSSProperties } from 'react';

import { formatMoney } from '../../lib/money';
import { getFinanceIcon } from './financeAppearance';
import { MoneyText } from './MoneyText';

type FinanceCoinProps = {
  name: string;
  amount: number;
  caption?: string;
  color: string;
  icon?: string;
  iconFallback?: string;
  progress?: number;
  danger?: boolean;
  mutedAmount?: boolean;
  active?: boolean;
  formatAmount?: (amount: number) => string;
};

export function FinanceCoin({
  name,
  amount,
  caption,
  color,
  icon,
  iconFallback = 'wallet',
  progress,
  danger = false,
  mutedAmount = false,
  active = false,
  formatAmount = formatMoney,
}: FinanceCoinProps) {
  const normalizedProgress =
    progress === undefined ? undefined : Math.min(Math.max(progress, 0), 1);
  const fillSurfaceY =
    normalizedProgress === undefined ? 58 : 58 * (1 - normalizedProgress);
  const fillAmplitude =
    normalizedProgress !== undefined &&
    normalizedProgress > 0.04 &&
    normalizedProgress < 0.96
      ? 2.2
      : 0;
  const fillPath = [
    `M 0 ${fillSurfaceY}`,
    `Q 7 ${fillSurfaceY - fillAmplitude} 14 ${fillSurfaceY}`,
    `Q 21 ${fillSurfaceY + fillAmplitude} 28 ${fillSurfaceY}`,
    `Q 35 ${fillSurfaceY - fillAmplitude} 42 ${fillSurfaceY}`,
    `Q 49 ${fillSurfaceY + fillAmplitude} 58 ${fillSurfaceY}`,
    'L 58 58',
    'L 0 58',
    'Z',
  ].join(' ');

  return (
    <span className={`finance-coin${active ? ' active' : ''}`}>
      <span className="finance-coin-name" title={name}>{name}</span>
      <span className="finance-coin-circle-wrap">
        <span
          className="finance-coin-circle"
          style={{
            '--coin-color': danger ? 'var(--negative)' : color,
            background:
              normalizedProgress === undefined
                ? color
                : 'var(--category-limit-track)',
          } as CSSProperties}
        >
          {normalizedProgress !== undefined && normalizedProgress > 0 && (
            <svg
              aria-hidden
              className="finance-coin-fill"
              viewBox="0 0 58 58"
              preserveAspectRatio="none"
            >
              <path d={fillPath} fill={danger ? 'var(--negative)' : color} />
            </svg>
          )}
          <span className="finance-coin-icon">
            {createElement(getFinanceIcon(icon, iconFallback), {
              size: 25,
              strokeWidth: 2.2,
            })}
          </span>
        </span>
      </span>
      <MoneyText
        fit
        className={[
          'finance-coin-amount',
          danger ? 'negative' : '',
          mutedAmount ? 'muted' : '',
        ].filter(Boolean).join(' ')}
        currencyClassName="finance-coin-currency"
        value={formatAmount(amount)}
      />
      {caption && (
        <span className={`finance-coin-caption${danger ? ' negative' : ''}`}>
          {caption}
        </span>
      )}
    </span>
  );
}
